import styled from 'styled-components';

import { colors } from 'constants/colors';

import { dateFromString, formatDateForDisplay } from 'helpers/dateTimes';

import {
    GetMicrostoreProductsQuery,
    HotDrinksInstructionFragmentFragment,
    MicrostoreColumnInventoryFragmentFragment,
    RemoveProductsInstructionFragmentFragment,
} from 'data/__generated__';
import { HotDrinksProductsSection } from 'pages/SiteSetups/details/components/hotDrinks/HotDrinksProductsSection';
import { RemovedProductsSection } from 'pages/SiteSetups/details/components/removeProducts/RemovedProductsSection';

export const InventoryProductsSection = ({
    checkedLocations,
    products,
    setSelectedLocationId,
    selectedLocationId,
    weekdateBeforeNextSetup,
    removedProducts,
    hotDrinksInstruction,
    inventoryDate,
}: {
    checkedLocations: MicrostoreColumnInventoryFragmentFragment['checkedLocations'];
    products: GetMicrostoreProductsQuery['getMicrostoreProducts'];
    setSelectedLocationId?: (selectedLocationId: string | null) => void;
    selectedLocationId?: string | null;
    weekdateBeforeNextSetup: Date;
    removedProducts?: RemoveProductsInstructionFragmentFragment['removedProducts'];
    hotDrinksInstruction?: HotDrinksInstructionFragmentFragment;
    inventoryDate: string;
}) => {
    const checkedProductIds = checkedLocations.map(({ productId }) => productId);
    const checkedProducts = products.filter((product) => checkedProductIds.includes(product._id));

    return (
        <ProductsContainer>
            {checkedProducts.map((checkedProduct) => {
                const associatedCheckedLocations = checkedLocations.filter(
                    ({ productId }) => productId === checkedProduct._id,
                );
                const isSelected = associatedCheckedLocations.some(
                    ({ microstoreLocationId }) => microstoreLocationId === selectedLocationId,
                );
                return (
                    <ProductContainer key={checkedProduct._id}>
                        <ProductName
                            onClick={() => {
                                const firstLocationId = associatedCheckedLocations[0]?.microstoreLocationId ?? null;
                                setSelectedLocationId?.(firstLocationId);
                            }}
                            $isSelected={isSelected}
                        >
                            {checkedProduct.name}
                        </ProductName>
                        <LocationsContainer>
                            {associatedCheckedLocations.map((checkedLocation, index) => {
                                return (
                                    <LocationContainer key={checkedLocation.microstoreLocationId}>
                                        <LocationName>Emplacement {index + 1}</LocationName>
                                        <QuantitiesContainer>
                                            {!checkedLocation.quantityDetails.length ? (
                                                <QuantityContainer>Vide</QuantityContainer>
                                            ) : null}
                                            {checkedLocation.quantityDetails.map(({ expiryDate, quantity }) => {
                                                const isExpired = !!(
                                                    expiryDate && dateFromString(expiryDate) < weekdateBeforeNextSetup
                                                );
                                                return (
                                                    <QuantityContainer key={expiryDate} $isExpired={isExpired}>
                                                        Inventaire: Qty: {quantity} ; DLC:{' '}
                                                        {expiryDate ? formatDateForDisplay(expiryDate) : 'Non Rensigné'}
                                                    </QuantityContainer>
                                                );
                                            })}
                                        </QuantitiesContainer>
                                    </LocationContainer>
                                );
                            })}
                        </LocationsContainer>
                    </ProductContainer>
                );
            })}
            <RemovedProductsSection
                removedProducts={removedProducts!}
                inventoryDate={inventoryDate}
                products={products}
                setSelectedLocationId={setSelectedLocationId}
                selectedLocationId={selectedLocationId}
            />
            {hotDrinksInstruction ? (
                <HotDrinksProductsSection
                    instruction={hotDrinksInstruction}
                    products={products}
                    selectedLocationId={selectedLocationId}
                    setSelectedLocationId={setSelectedLocationId}
                />
            ) : null}
        </ProductsContainer>
    );
};

const ProductsContainer = styled.div`
    flex-grow: 1;
    & > :not(:first-child) {
        border-top: 1px solid ${colors.lightGrey};
        margin-top: 5px;
    }
`;
const ProductContainer = styled.div`
    display: flex;
    justify-content: space-between;
`;
const ProductName = styled.div<{ $isSelected: boolean }>`
    cursor: pointer;
    font-weight: bold;
    width: 50%;
    color: ${({ $isSelected }) => ($isSelected ? colors.greenDarker : colors.pureBlack)};
`;
const LocationsContainer = styled.div`
    margin-left: 10px;
    width: 50%;
`;
const LocationContainer = styled.div`
    display: flex;
`;
const LocationName = styled.div`
    font-style: italic;
`;
const QuantitiesContainer = styled.div``;
const QuantityContainer = styled.div<{ $isExpired?: boolean }>`
    margin-left: 10px;
    color: ${({ $isExpired }) => ($isExpired ? colors.red : colors.pureBlack)};
`;
